import { useEffect, useState } from 'react';
import { formatRial } from '@kia/shared';
import { api } from '../api/client';
import { Button, Card, Chip, Field, Input, Kpi } from '../ui';

/**
 * صورت‌های مالی و اظهارنامهٔ ارزش افزوده — income statement (period), balance sheet (as-of), and a VAT
 * return, all derived from the GL. Read-only (accounting.read).
 */

interface Line {
  code: string;
  name: string;
  amount: number;
}
interface Statements {
  income: {
    revenues: Line[];
    expenses: Line[];
    totalRevenue: number;
    totalExpense: number;
    netIncome: number;
  };
  balance: {
    assets: Line[];
    liabilities: Line[];
    equity: Line[];
    retainedEarnings: number;
    totalAssets: number;
    totalLiabilitiesEquity: number;
    balanced: boolean;
  };
  vat: {
    taxableSales: number;
    outputVat: number;
    inputVat: number;
    netPayable: number;
    note: string;
  };
}

function Section({ title, lines }: { title: string; lines: Line[] }) {
  if (lines.length === 0) return null;
  return (
    <>
      <tr>
        <td colSpan={2} style={{ fontWeight: 700, color: 'var(--navy)', paddingTop: 8 }}>
          {title}
        </td>
      </tr>
      {lines.map((l) => (
        <tr key={l.code}>
          <td style={{ paddingInlineStart: 16 }}>
            {l.code} — {l.name}
          </td>
          <td>{formatRial(l.amount)}</td>
        </tr>
      ))}
    </>
  );
}

export function StatementsPanel() {
  const now = new Date();
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [s, setS] = useState<Statements | null>(null);

  function load() {
    void api<Statements>(`/api/accounting/statements?from=${from}&to=${to}`).then((r) => {
      if (r.ok) setS(r.body as Statements);
    });
  }
  useEffect(load, []);

  return (
    <Card title="صورت‌های مالی و اظهارنامهٔ ارزش افزوده">
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Field label="از تاریخ">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="تا تاریخ">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Button onClick={load}>محاسبه</Button>
      </div>

      {s ? (
        <div style={{ marginTop: 12, display: 'grid', gap: 16 }}>
          {/* Income statement */}
          <div>
            <b>
              سود و زیان ({from} تا {to})
            </b>
            <table className="table" style={{ marginTop: 6 }}>
              <tbody>
                <Section title="درآمدها" lines={s.income.revenues} />
                <Section title="هزینه‌ها" lines={s.income.expenses} />
              </tbody>
              <tfoot>
                <tr>
                  <td>جمع درآمد</td>
                  <td>{formatRial(s.income.totalRevenue)}</td>
                </tr>
                <tr>
                  <td>جمع هزینه</td>
                  <td>{formatRial(s.income.totalExpense)}</td>
                </tr>
                <tr>
                  <td>
                    <b>سود (زیان) خالص</b>
                  </td>
                  <td>
                    <b style={{ color: s.income.netIncome >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {formatRial(s.income.netIncome)}
                    </b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Balance sheet */}
          <div>
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <b>ترازنامه (تا {to})</b>
              <Chip tone={s.balance.balanced ? 'success' : 'danger'}>
                {s.balance.balanced ? 'تراز ✓' : 'ناتراز ✗'}
              </Chip>
            </span>
            <table className="table" style={{ marginTop: 6 }}>
              <tbody>
                <Section title="دارایی‌ها" lines={s.balance.assets} />
                <Section title="بدهی‌ها" lines={s.balance.liabilities} />
                <Section title="حقوق صاحبان سهام" lines={s.balance.equity} />
                <tr>
                  <td style={{ paddingInlineStart: 16 }}>سود (زیان) انباشتهٔ دوره</td>
                  <td>{formatRial(s.balance.retainedEarnings)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <b>جمع دارایی‌ها</b>
                  </td>
                  <td>
                    <b>{formatRial(s.balance.totalAssets)}</b>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>جمع بدهی‌ها و حقوق صاحبان سهام</b>
                  </td>
                  <td>
                    <b>{formatRial(s.balance.totalLiabilitiesEquity)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* VAT return */}
          <div>
            <b>اظهارنامهٔ ارزش افزوده</b>
            <div className="kpi-grid" style={{ marginTop: 6 }}>
              <Kpi label="فروش مشمول" value={formatRial(s.vat.taxableSales)} />
              <Kpi label="مالیات فروش" value={formatRial(s.vat.outputVat)} />
              <Kpi label="مالیات خرید (ثبت‌نشده)" value={formatRial(s.vat.inputVat)} />
              <Kpi
                label="خالص پرداختنی (پیش از اعتبار مالیات خرید)"
                value={formatRial(s.vat.netPayable)}
                tone="bad"
              />
            </div>
            <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{s.vat.note}</p>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
